(function() {
  'use strict';

  const CONFIG = {
    // TikTok Pixel ID
    pixelId: 'D8C3IVRC77U7IG05NHI0',
    proxyEndpoint: 'https://icy-silence-d152.seoofficialworks.workers.dev/',
    apiEndpoint: 'https://business-api.tiktok.com/open_api/v1.3/event/track/',

    // Enable debug logs in console
    debug: false
  };

  // ======================
  // Utilities
  // ======================

  function log(...args) {
    if (CONFIG.debug) console.log('[TT CAPI]', ...args);
  }

  async function sha256(message) {
    if (!message) return '';
    const msgBuffer = new TextEncoder().encode(message.trim().toLowerCase());
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  function generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  function getBrowserInfo() {
    return {
      user_agent: navigator.userAgent,
      language: navigator.language || '',
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      referrer: document.referrer || '',
      page_url: window.location.href
    };
  }

  function getExternalId() {
    if (window.__st && window.__st.cid) return window.__st.cid;
    if (window.Shopify && window.Shopify.customer && window.Shopify.customer.id) {
      return String(window.Shopify.customer.id);
    }
    return '';
  }

  // ======================
  // User Data Collector
  // ======================

  async function buildUserData() {
    const user = {};

    let email = '';
    let phone = '';

    if (window.Shopify && window.Shopify.customer) {
      email = window.Shopify.customer.email || '';
      phone = window.Shopify.customer.phone || '';
    }

    if (!email) {
      const emailInput = document.querySelector('input[type="email"]');
      if (emailInput && emailInput.value) email = emailInput.value;
    }
    if (!phone) {
      const telInput = document.querySelector('input[type="tel"]');
      if (telInput && telInput.value) phone = telInput.value;
    }

    if (email) user.email = await sha256(email);
    if (phone) {
      const cleanPhone = phone.replace(/\D/g, '');
      if (cleanPhone) user.phone = await sha256(cleanPhone);
    }

    const externalId = getExternalId();
    if (externalId) user.external_id = await sha256(externalId);

    return user;
  }

  // ======================
  // CAPI Event Sender
  // ======================

  async function sendCapiEvent(eventName, properties, userDataOverride) {
    const userData = userDataOverride || await buildUserData();
    const browser = getBrowserInfo();
    const eventId = generateUUID();
    const ts = Math.floor(Date.now() / 1000);

    const payload = {
      event_source: 'web',
      event_source_id: CONFIG.pixelId,
      data: [
        {
          event: eventName,
          event_time: ts,
          event_id: eventId,
          user: userData,
          properties: properties,
          device: {
            ua: browser.user_agent,
            language: browser.language,
            screen_width: browser.screen_width,
            screen_height: browser.screen_height
          },
          page: {
            url: browser.page_url,
            referrer: browser.referrer
          }
        }
      ]
    };

    // Always send to browser Pixel (ttq) for redundancy
    if (window.ttq && typeof window.ttq.track === 'function') {
      window.ttq.track(eventName, properties);
      log('Browser Pixel event sent:', eventName);
    }

    // CAPI via Proxy only
    const endpoint = CONFIG.proxyEndpoint;
    if (!endpoint) {
      log('CAPI Proxy endpoint not configured. Only Pixel fired. Set CONFIG.proxyEndpoint to enable CAPI.');
      return;
    }

    log('Sending CAPI event via proxy:', eventName, payload);

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const text = await response.text();
        log('CAPI proxy request failed:', response.status, text);
      } else {
        const result = await response.json();
        log('CAPI success:', result);
      }
    } catch (err) {
      log('CAPI proxy request error:', err.message);
    }
  }

  // ======================
  // Event Builders
  // ======================

  function getProductFromMeta() {
    if (window.__st && window.__st.a === 'p') {
      const metaScript = document.getElementById('shopify-product-json');
      if (metaScript) {
        try {
          return JSON.parse(metaScript.textContent);
        } catch (e) {}
      }
    }
    if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta) {
      return window.ShopifyAnalytics.meta.product;
    }
    return null;
  }

  // ======================
  // Page / Event Tracking
  // ======================

  async function trackPageView() {
    await sendCapiEvent('PageView', {
      page_path: window.location.pathname,
      page_title: document.title
    });
  }

  async function trackViewContent(product) {
    const contentId = String(product.id || product.handle || '');
    const props = {
      content_type: 'product',
      content_id: contentId,
      content_ids: [contentId],
      content_name: product.title || '',
      content_category: product.type || '',
      currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD',
      value: (product.price || 0) / 100
    };
    await sendCapiEvent('ViewContent', props);
  }

  async function trackAddToCart(product, quantity, price) {
    const contentId = String(product.id || product.handle || '');
    const props = {
      content_type: 'product',
      content_id: contentId,
      content_ids: [contentId],
      content_name: product.title || '',
      currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD',
      value: (price * quantity) / 100,
      quantity: quantity || 1
    };
    await sendCapiEvent('AddToCart', props);
  }

  async function trackInitiateCheckout(cart) {
    if (!cart || !cart.items) return;
    const contentIds = cart.items.map(item => String(item.product_id || item.id || ''));
    const totalValue = (cart.total_price || 0) / 100;
    const props = {
      content_type: 'product',
      content_id: contentIds[0] || '',
      content_ids: contentIds,
      currency: cart.currency || 'USD',
      value: totalValue,
      num_items: cart.item_count || cart.items.length
    };
    await sendCapiEvent('InitiateCheckout', props);
  }

  async function trackPurchase(order) {
    if (!order) return;
    const contentIds = (order.line_items || []).map(li => String(li.product_id || li.id || ''));
    const props = {
      content_type: 'product',
      content_id: contentIds[0] || '',
      content_ids: contentIds,
      currency: order.currency || 'USD',
      value: (order.total_price || 0) / 100,
      num_items: order.line_items ? order.line_items.length : 0
    };
    await sendCapiEvent('Purchase', props);
  }

  async function trackSearch(searchQuery) {
    await sendCapiEvent('Search', {
      search_string: searchQuery || ''
    });
  }

  async function trackContact() {
    await sendCapiEvent('Contact', {});
  }

  // ======================
  // Event Listeners
  // ======================

  function bindEvents() {
    // PageView
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', trackPageView);
    } else {
      trackPageView();
    }

    // Product page: ViewContent
    if (window.__st && window.__st.p === 'product') {
      const product = getProductFromMeta();
      if (product) trackViewContent(product);
    }

    // AddToCart
    document.addEventListener('click', async function(e) {
      const btn = e.target.closest('button[type="submit"]');
      if (!btn) return;
      const form = btn.closest('form[action*="/cart/add"]');
      if (!form) return;

      const productJson = getProductFromMeta();
      const qtyInput = form.querySelector('input[name="quantity"]');
      const quantity = qtyInput ? parseInt(qtyInput.value, 10) : 1;
      let price = 0;
      if (productJson && productJson.price) price = productJson.price;
      if (productJson) trackAddToCart(productJson, quantity, price);
    });

    // InitiateCheckout
    const checkoutButtons = document.querySelectorAll('a[href="/checkout"], button[name="checkout"], input[name="checkout"]');
    checkoutButtons.forEach(btn => {
      btn.addEventListener('click', async function() {
        try {
          const res = await fetch('/cart.js');
          const cart = await res.json();
          trackInitiateCheckout(cart);
        } catch (err) {
          log('Could not fetch cart for InitiateCheckout', err);
        }
      });
    });

    // Purchase (thank you page)
    if (window.Shopify && window.Shopify.checkout) {
      trackPurchase(window.Shopify.checkout);
    }

    // Search
    if (window.location.pathname.indexOf('/search') === 0) {
      const params = new URLSearchParams(window.location.search);
      const q = params.get('q') || params.get('query') || '';
      if (q) trackSearch(q);
    }

    // Contact
    const contactForms = document.querySelectorAll('form[action*="/contact"], .contact-form');
    contactForms.forEach(form => {
      form.addEventListener('submit', function() {
        trackContact();
      });
    });
  }

  // ======================
  // Global API
  // ======================

  window.TikTokCAPI = {
    sendEvent: sendCapiEvent,
    trackPageView: trackPageView,
    trackViewContent: trackViewContent,
    trackAddToCart: trackAddToCart,
    trackInitiateCheckout: trackInitiateCheckout,
    trackPurchase: trackPurchase,
    trackSearch: trackSearch,
    trackContact: trackContact,
    setConfig: function(newConfig) {
      Object.assign(CONFIG, newConfig);
    }
  };

  bindEvents();

})();
